import * as Y from 'yjs';
import { IndexeddbPersistence } from 'y-indexeddb';
import { SCHEMA_VERSION, SceneStore } from '@os/doc';

export type LocalBoard = {
  store: SceneStore;
  whenSynced: Promise<void>;
  destroy(): void;
  /** Deletes this device's copy of the board (a guest whose access was revoked, P6). */
  forget(): Promise<void>;
};
export type Readable = { objects: unknown[] };

export class BoardOpenError extends Error {
  constructor(
    readonly code: 'corrupt' | 'schema-too-new',
    /** Whatever could still be decoded, offered to the user as a JSON export. */
    readonly readable: Readable,
  ) {
    super(code);
  }
}

const dbName = (boardId: string) => `board:${boardId}`;
const INIT_ORIGIN = Symbol('init'); // not LOCAL_ORIGIN: initialising a board is not an undo step

/** Reads the raw y-indexeddb updates without the library (which never settles on a corrupted update). */
async function readUpdates(name: string): Promise<Uint8Array[]> {
  const db = await new Promise<IDBDatabase>((resolve, reject) => {
    const req = indexedDB.open(name);
    req.onupgradeneeded = () => {
      // Same stores as y-indexeddb, so a board first opened here stays readable by it.
      req.result.createObjectStore('updates', { autoIncrement: true });
      req.result.createObjectStore('custom');
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  try {
    const req = db.transaction('updates', 'readonly').objectStore('updates').getAll();
    return await new Promise<Uint8Array[]>((resolve, reject) => {
      req.onsuccess = () => resolve(req.result as Uint8Array[]);
      req.onerror = () => reject(req.error);
    });
  } finally {
    db.close();
  }
}

function readableObjects(doc: Y.Doc): unknown[] {
  try {
    return [...doc.getMap('objects').values()].map((v) => (v instanceof Y.AbstractType ? v.toJSON() : v));
  } catch {
    return [];
  }
}

/** Dry run on a scratch document: nothing is written until the persisted board is known to be readable. */
async function check(name: string): Promise<void> {
  const scratch = new Y.Doc();
  let corrupt = false;
  for (const update of await readUpdates(name)) {
    try {
      Y.applyUpdate(scratch, update);
    } catch {
      corrupt = true;
    }
  }
  let version: unknown;
  try {
    scratch.getMap('objects');
    version = scratch.getMap('meta').get('schemaVersion');
  } catch {
    corrupt = true;
  }
  if (!corrupt && version !== undefined && typeof version !== 'number') corrupt = true;
  const readable = { objects: readableObjects(scratch) };
  scratch.destroy();
  if (corrupt) throw new BoardOpenError('corrupt', readable);
  if (typeof version === 'number' && version > SCHEMA_VERSION) throw new BoardOpenError('schema-too-new', readable);
}

/** Deletes this device's copy of a board that is not open (the server no longer shows it, P6). */
export function forgetLocalBoard(boardId: string): Promise<void> {
  try {
    localStorage.removeItem(journalKey(boardId));
  } catch {
    // nothing stored
  }
  return new Promise((resolve) => {
    const req = indexedDB.deleteDatabase(dbName(boardId));
    req.onsuccess = req.onerror = () => resolve();
  });
}

const journalKey = (boardId: string) => `board-journal:${boardId}`;
/** Characters one journal may take in localStorage (~5 MB per origin, shared with the Supabase session). */
const JOURNAL_MAX = 256 * 1024;
const toBase64 = (b: Uint8Array) => {
  let s = '';
  for (let i = 0; i < b.length; i += 0x8000) s += String.fromCharCode(...b.subarray(i, i + 0x8000));
  return btoa(s);
};
const fromBase64 = (s: string) => Uint8Array.from(atob(s), (c) => c.charCodeAt(0));

/**
 * The changes IndexedDB has not committed yet, written synchronously to localStorage when the page goes away (P12a).
 * y-indexeddb writes each update in its own transaction and a reload aborts the ones still in flight: typing then
 * reloading at once kept only the first letter. A readwrite transaction on the same store completes only after
 * the earlier ones, so its `complete` marks everything before it as safe.
 */
function journal(boardId: string, doc: Y.Doc, persistence: IndexeddbPersistence): (() => void) & { track(update: Uint8Array): void } {
  let pending: Uint8Array[] = [];
  let checking = false;
  const check = () => {
    const db = persistence.db;
    if (checking || !db || pending.length === 0) return;
    checking = true;
    const upTo = pending.length;
    let tx: IDBTransaction;
    try {
      tx = db.transaction('updates', 'readwrite');
    } catch {
      checking = false; // closed: the page is going away, pagehide keeps what is pending
      return;
    }
    tx.oncomplete = () => {
      pending = pending.slice(upTo);
      checking = false;
      check();
    };
    tx.onabort = tx.onerror = () => void (checking = false);
  };
  const track = (update: Uint8Array) => {
    pending.push(update);
    queueMicrotask(check); // after y-indexeddb has opened its own transaction for this update
  };
  // Only this tab's own changes: what IndexedDB loaded, a peer sent or the room's state is safe elsewhere.
  const onUpdate = (update: Uint8Array, _origin: unknown, _doc: Y.Doc, tr: Y.Transaction) => {
    if (tr.local) track(update);
  };
  const save = () => {
    if (pending.length === 0) return;
    try {
      const text = toBase64(Y.mergeUpdates(pending));
      // localStorage also holds the session: a journal never takes more than its share.
      if (text.length > JOURNAL_MAX) return void console.warn(`board journal skipped: ${text.length} characters`);
      localStorage.setItem(journalKey(boardId), text);
    } catch {
      // storage full or blocked: IndexedDB may still have committed them
    }
  };
  const onHidden = () => document.visibilityState === 'hidden' && save();
  doc.on('update', onUpdate);
  addEventListener('pagehide', save);
  document.addEventListener('visibilitychange', onHidden); // a phone may kill a hidden tab without pagehide
  return Object.assign(
    () => {
      doc.off('update', onUpdate);
      removeEventListener('pagehide', save);
      document.removeEventListener('visibilitychange', onHidden);
    },
    { track },
  );
}

/**
 * Replays the journal a previous page left (Yjs ignores what IndexedDB already holds) and tracks it like a change of
 * this tab, so it is journaled again until IndexedDB commits it.
 */
function replayJournal(boardId: string, doc: Y.Doc, track: (update: Uint8Array) => void): void {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(journalKey(boardId));
    localStorage.removeItem(journalKey(boardId));
  } catch {
    return;
  }
  if (!raw) return;
  try {
    const update = fromBase64(raw);
    Y.applyUpdate(doc, update, INIT_ORIGIN);
    track(update);
  } catch {
    // a damaged journal is dropped; IndexedDB's copy stands
  }
}

/**
 * Opens a board stored on this device (Y.Doc + y-indexeddb). Undo starts empty. A board written by a newer
 * schema or with an undecodable update is refused before anything is written (BoardOpenError).
 */
export async function openLocalBoard(boardId: string): Promise<LocalBoard> {
  const name = dbName(boardId);
  await check(name);
  const doc = new Y.Doc();
  const persistence = new IndexeddbPersistence(name, doc);
  await persistence.whenSynced;
  const stopJournal = journal(boardId, doc, persistence);
  replayJournal(boardId, doc, stopJournal.track);
  const meta = doc.getMap('meta');
  if (meta.get('schemaVersion') === undefined) {
    doc.transact(() => {
      meta.set('schemaVersion', SCHEMA_VERSION);
      meta.set('epoch', 0); // used by sync from P3 on, ignored locally
    }, INIT_ORIGIN);
  }
  return {
    store: new SceneStore(doc),
    whenSynced: Promise.resolve(),
    destroy: () => {
      stopJournal();
      void persistence.destroy();
      doc.destroy();
    },
    forget: () => {
      stopJournal();
      try {
        localStorage.removeItem(journalKey(boardId));
      } catch {
        // nothing stored
      }
      return persistence.clearData();
    },
  };
}
